import * as React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router-dom";
import landingBg from "../../assets/images/landing_bg.png";
import { allowlistActive, canUserSubscribe } from "../../config/pricing-access";
import { hasLocallyValidSession } from "../../services/auth/auth-session";
import { fetchMyProfile } from "../../services/auth/profile-api";
import "./styles.css";

const HOW_IT_WORKS = [
  {
    icon: "🔍",
    title: "Find a Game",
    description: "Search games near you by rink, date, or skill level.",
  },
  {
    icon: "✍️",
    title: "Sign Up & Pay",
    description: "Reserve your spot and pay securely in seconds.",
  },
  {
    icon: "🏒",
    title: "Hit the Ice",
    description: "Show up and play. No long-term commitment required.",
  },
];

export default function GettingStartedLandingView() {
  const navigate = useNavigate();
  const [showPricingCta, setShowPricingCta] = React.useState(!allowlistActive);

  React.useEffect(() => {
    if (!allowlistActive) return;

    const check = () => {
      if (!hasLocallyValidSession()) { setShowPricingCta(false); return; }
      fetchMyProfile()
        .then((profile) => setShowPricingCta(canUserSubscribe(profile?.email)))
        .catch(() => setShowPricingCta(false));
    };

    check();
    window.addEventListener("puck-pickup-auth-change", check);
    return () => window.removeEventListener("puck-pickup-auth-change", check);
  }, []);

  return (
    <>
      <Box className="landing-hero">
        <Box className="landing-hero-bg" sx={{ backgroundImage: `url(${landingBg})` }} />
        <Box className="bg-overlay" />
        <Stack className="landing-hero-content" spacing={2.5}>
          <Typography component="h3" className="landing-hero-title">
            The Easiest Way to Play Hockey
          </Typography>
          <Typography className="landing-hero-subtitle">
            Find games. Host games. Hit the ice.
          </Typography>
          <Typography className="landing-hero-social-proof">
            Trusted by leagues and pickup players across the US
          </Typography>
          <Stack direction="row" spacing={2} className="landing-hero-ctas">
            <Button
              className="landing-hero-cta landing-hero-cta-primary"
              onClick={() => navigate("/search")}
              size="large"
            >
              Browse Games
            </Button>
            {showPricingCta && (
              <Button
                className="landing-hero-cta landing-hero-cta-secondary"
                onClick={() => navigate("/pricing")}
                size="large"
              >
                Host a League
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ py: 8, px: 2 }}>
        <Typography component="h3" className="landing-how-title">
          How it works
        </Typography>
        <Box className="landing-how-grid">
          {HOW_IT_WORKS.map((step) => (
            <Box
              key={step.title}
              className="landing-how-card"
              sx={{ bgcolor: "background.paper", borderColor: "divider" }}
            >
              <span className="landing-how-icon">{step.icon}</span>
              <Typography className="landing-how-card-title">{step.title}</Typography>
              <Typography className="landing-how-card-desc" sx={{ color: "text.secondary" }}>
                {step.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
}
